import { useTranslation } from 'react-i18next';
import { Coins, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDisplayCurrency } from '../hooks/useDisplayCurrency';

/**
 * M4.c (AB#927): Topbar'da gösterim para birimi seçici. Sadece dashboard widget'larının
 * agrega kart toplamlarını etkiler — ham veri saklama katmanı dokunulmaz.
 *
 * Seçim `useDisplayCurrency` hook'una delege edilir; persist + cross-component sync
 * orada yaşar.
 */
export default function CurrencySwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { displayCurrency, setDisplayCurrency, supportedCurrencies } = useDisplayCurrency();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('topbar.displayCurrency.aria')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium',
            'border border-border bg-transparent text-muted-foreground',
            'hover:bg-accent hover:text-foreground',
            'transition-[background-color,color,transform] duration-150 ease-out-strong',
            'motion-safe:active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <Coins size={15} aria-hidden="true" />
          <span className="tabular-nums">{displayCurrency}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-48">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('topbar.displayCurrency.label')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supportedCurrencies.map((code) => {
          const isActive = code === displayCurrency;
          return (
            <DropdownMenuItem
              key={code}
              onSelect={() => setDisplayCurrency(code)}
              className="flex items-center justify-between"
            >
              <span className="tabular-nums">{code}</span>
              {isActive && <Check size={14} className="text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setDisplayCurrency(null)} className="text-xs text-muted-foreground">
          {t('topbar.displayCurrency.reset')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
